/**
 * AllegroConnectPanel – Allegro marketplace integration panel.
 * OAuth flow, offers & sales fetch, context injection.
 */
import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { apiRequest } from "@/lib/queryClient";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Loader2, ShoppingBag, TrendingUp, Package, ChevronDown, ChevronUp } from "lucide-react";

interface AllegroStatus {
  connected: boolean;
  allegroLogin?: string;
  isSandbox?: boolean;
}

interface AllegroConnectPanelProps {
  onInsightsLoaded?: (context: string) => void;
  forUserId?: string;
}

const AllegroLogo = () => (
  <div className="w-6 h-6 rounded bg-[#FF5A00] flex items-center justify-center">
    <span className="text-white font-bold text-xs">A</span>
  </div>
);

export default function AllegroConnectPanel({
  onInsightsLoaded,
  forUserId,
}: AllegroConnectPanelProps) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [insightsData, setInsightsData] = useState<any>(null);
  const [showDetails, setShowDetails] = useState(false);
  const fuParam = forUserId ? `?forUserId=${forUserId}` : "";

  const { data: status, isLoading } = useQuery<AllegroStatus>({
    queryKey: ["allegro-integration-status", forUserId],
    queryFn: () => apiRequest("GET", `/api/integrations/allegro/status${fuParam}`),
    staleTime: 30_000,
  });

  useEffect(() => {
    const handler = (event: MessageEvent) => {
      if (event.data?.type === "ALLEGRO_OAUTH_SUCCESS") {
        queryClient.invalidateQueries({ queryKey: ["allegro-integration-status"] });
      }
    };
    window.addEventListener("message", handler);
    return () => window.removeEventListener("message", handler);
  }, [queryClient]);

  const connectMutation = useMutation({
    mutationFn: () => apiRequest("GET", "/api/integrations/allegro/connect"),
    onSuccess: (data: { url: string }) => {
      const w = 600,
        h = 700;
      const left = window.screenX + (window.outerWidth - w) / 2;
      const top = window.screenY + (window.outerHeight - h) / 2;
      const popup = window.open(
        data.url,
        "allegro_oauth",
        `width=${w},height=${h},left=${left},top=${top},toolbar=no,menubar=no`
      );
      const check = setInterval(() => {
        if (popup?.closed) {
          clearInterval(check);
          queryClient.invalidateQueries({ queryKey: ["allegro-integration-status"] });
        }
      }, 500);
    },
  });

  const disconnectMutation = useMutation({
    mutationFn: () => apiRequest("POST", "/api/integrations/allegro/disconnect"),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["allegro-integration-status"] });
      setInsightsData(null);
    },
  });

  const insightsMutation = useMutation({
    mutationFn: () => apiRequest("POST", "/api/integrations/allegro/insights"),
    onSuccess: (data: any) => {
      setInsightsData(data);
      onInsightsLoaded?.(data.context);
    },
  });

  if (isLoading) {
    return (
      <div className="p-3 bg-white/[0.04] border border-white/[0.08] rounded-lg flex items-center gap-2">
        <Loader2 className="h-4 w-4 animate-spin text-orange-400" />
        <span className="text-sm text-white/60">{t("connectPanels.allegro.checking")}</span>
      </div>
    );
  }

  if (!status?.connected) {
    return (
      <div className="p-3 bg-white/[0.04] border border-white/[0.08] rounded-lg space-y-2">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <AllegroLogo />
            <span className="text-sm font-medium text-white">Allegro</span>
            <Badge variant="outline" className="text-xs border-white/[0.1] text-white/60">
              {t("connectPanels.common.notConnected")}
            </Badge>
          </div>
          <Button
            size="sm"
            onClick={() => connectMutation.mutate()}
            disabled={connectMutation.isPending}
            className="bg-[#FF5A00] hover:bg-[#e65100] text-white text-xs"
          >
            {connectMutation.isPending ? (
              <>
                <Loader2 className="h-3 w-3 animate-spin mr-1" />{" "}
                {t("connectPanels.common.connecting")}
              </>
            ) : (
              t("connectPanels.allegro.connect")
            )}
          </Button>
        </div>
        <p className="text-xs text-white/40">{t("connectPanels.allegro.hint")}</p>
      </div>
    );
  }

  return (
    <div className="p-3 bg-white/[0.04] border border-[#FF5A00]/30 rounded-lg space-y-3">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <AllegroLogo />
          <span className="text-sm font-medium text-white">Allegro</span>
          <Badge className="text-xs bg-green-600/20 text-green-400 border-green-500/50">
            {t("connectPanels.common.connected")}
            {status.allegroLogin ? ` · ${status.allegroLogin}` : ""}
          </Badge>
          {status.isSandbox && (
            <Badge className="text-xs bg-amber-600/20 text-amber-400 border-amber-500/50">
              {t("connectPanels.allegro.sandbox")}
            </Badge>
          )}
        </div>
        <Button
          size="sm"
          variant="ghost"
          onClick={() => disconnectMutation.mutate()}
          disabled={disconnectMutation.isPending}
          className="text-xs text-red-400 hover:text-red-300 hover:bg-red-500/10"
        >
          {t("connectPanels.common.disconnect")}
        </Button>
      </div>

      <div className="flex items-center gap-2">
        <Button
          size="sm"
          onClick={() => insightsMutation.mutate()}
          disabled={insightsMutation.isPending}
          className="bg-[#FF5A00] hover:bg-[#e65100] text-white text-xs"
        >
          {insightsMutation.isPending ? (
            <>
              <Loader2 className="h-3 w-3 animate-spin mr-1" /> {t("connectPanels.common.fetching")}
            </>
          ) : (
            t("connectPanels.common.fetch")
          )}
        </Button>
      </div>

      {insightsData && (
        <>
          <div className="grid grid-cols-3 gap-2">
            <div className="bg-orange-500/10 border border-orange-500/20 rounded-xl p-3">
              <div className="flex items-center gap-2">
                <Package className="h-3.5 w-3.5 text-orange-400" />
                <span className="text-[11px] text-white/60 uppercase">
                  {t("connectPanels.allegro.offers")}
                </span>
              </div>
              <div className="text-xl font-bold text-orange-400 mt-1">
                {insightsData.offers?.length || 0}
              </div>
            </div>
            <div className="bg-emerald-500/10 border border-emerald-500/20 rounded-xl p-3">
              <div className="flex items-center gap-2">
                <ShoppingBag className="h-3.5 w-3.5 text-emerald-400" />
                <span className="text-[11px] text-white/60 uppercase">
                  {t("connectPanels.allegro.sales")}
                </span>
              </div>
              <div className="text-xl font-bold text-emerald-400 mt-1">
                {insightsData.salesStats?.totalSold || 0}
              </div>
            </div>
            <div className="bg-blue-500/10 border border-blue-500/20 rounded-xl p-3">
              <div className="flex items-center gap-2">
                <TrendingUp className="h-3.5 w-3.5 text-blue-400" />
                <span className="text-[11px] text-white/60 uppercase">
                  {t("connectPanels.common.revenue")}
                </span>
              </div>
              <div className="text-xl font-bold text-blue-400 mt-1">
                {(insightsData.salesStats?.totalRevenue || 0).toFixed(0)} PLN
              </div>
            </div>
          </div>

          {insightsData.offers?.length > 0 && (
            <div>
              <button
                onClick={() => setShowDetails(!showDetails)}
                className="flex items-center gap-1.5 text-xs text-white/60 hover:text-white/70 transition-colors w-full"
              >
                {showDetails ? (
                  <ChevronUp className="h-3.5 w-3.5" />
                ) : (
                  <ChevronDown className="h-3.5 w-3.5" />
                )}
                <span className="font-medium">
                  {t("connectPanels.allegro.topOffers", {
                    count: Math.min(insightsData.offers.length, 10),
                  })}
                </span>
              </button>
              {showDetails && (
                <div className="mt-2 rounded-lg border border-white/[0.08] overflow-hidden">
                  <table className="w-full text-xs">
                    <thead>
                      <tr className="bg-[#0a0c10]/80 text-white/60">
                        <th className="text-left p-2 font-medium">
                          {t("connectPanels.common.name")}
                        </th>
                        <th className="text-right p-2 font-medium">
                          {t("connectPanels.common.price")}
                        </th>
                        <th className="text-right p-2 font-medium">
                          {t("connectPanels.common.sold")}
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {insightsData.offers.slice(0, 10).map((o: any, i: number) => (
                        <tr key={i} className="border-t border-white/[0.06] hover:bg-[#0a0c10]/40">
                          <td className="p-2 text-white max-w-[250px] truncate">{o.name}</td>
                          <td className="p-2 text-right text-orange-400">
                            {o.price?.amount || "—"} {o.price?.currency || "PLN"}
                          </td>
                          <td className="p-2 text-right text-emerald-400">
                            {o.stats?.soldCount || "—"}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}

          <div className="flex items-center gap-2">
            <Badge className="text-xs bg-green-600/20 text-green-400 border-green-500/50">
              {t("connectPanels.common.dataLoaded")}
            </Badge>
            <span className="text-xs text-white/40">
              {t("connectPanels.allegro.contextAttached")}
            </span>
          </div>
        </>
      )}
    </div>
  );
}
